import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';
import { ROLES } from '../contexts/AuthContext';

const ALL_ROLES = [
  { value: ROLES.BUYER, label: 'Buyer' },
  { value: ROLES.INVENTORY_MANAGER, label: 'Inventory Manager' },
  { value: ROLES.CASHIER, label: 'Cashier (Sales Desk)' },
  { value: ROLES.ADMINISTRATOR, label: 'Administrator' },
  { value: ROLES.SUPER_USER, label: 'Super User' },
];

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: true,
  });
};

const roleVariant = (role) => {
  switch (role) {
    case ROLES.SUPER_USER: return 'danger';
    case ROLES.ADMINISTRATOR: return 'warning';
    case ROLES.INVENTORY_MANAGER: return 'info';
    case ROLES.BUYER: return 'secondary';
    default: return 'success';
  }
};

const SuperUserUserManager = () => {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });
  const [actionLoading, setActionLoading] = useState(false);

  const loadUsers = async (searchValue = '', roleValue = '') => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (searchValue.trim()) params.search = searchValue.trim();
      if (roleValue) params.role = roleValue;
      const response = await axios.get('/api/user-management/users', { params });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const applySearch = async (event) => {
    event.preventDefault();
    await loadUsers(search, roleFilter);
  };

  const clearSearch = async () => {
    setSearch('');
    setRoleFilter('');
    await loadUsers('', '');
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const openRolesModal = (user) => {
    setSelectedUser(user);
    const currentRoles = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles : [user.role];
    setSelectedRoles(currentRoles);
    setShowRolesModal(true);
  };

  const toggleRole = (roleValue) => {
    setSelectedRoles((prev) =>
      prev.includes(roleValue) ? prev.filter((r) => r !== roleValue) : [...prev, roleValue],
    );
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (!newPassword || newPassword.trim().length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      const response = await axios.post(
        `/api/user-management/users/${selectedUser.id}/reset-password`,
        { new_password: newPassword.trim() },
      );
      setSuccess(response.data?.message || 'Password reset successfully');
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (actionError) {
      setError(actionError.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;
    if (selectedRoles.length === 0) {
      setError('At least one role must be selected.');
      return;
    }
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      const response = await axios.put(
        `/api/user-management/users/${selectedUser.id}/roles`,
        { roles: selectedRoles },
      );
      setSuccess(response.data?.message || 'Roles updated successfully');
      setShowRolesModal(false);
      setSelectedUser(null);
      await loadUsers(search, roleFilter);
    } catch (actionError) {
      setError(actionError.response?.data?.detail || 'Failed to update roles');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSuspendUser = async (user) => {
    try {
      setActionLoading(true);
      setError('');
      setSuccess('');
      const response = await axios.post(`/api/user-management/users/${user.id}/suspend`, {
        is_suspended: !user.is_suspended,
      });
      setSuccess(response.data?.message || 'User status updated successfully');
      await loadUsers(search, roleFilter);
    } catch (actionError) {
      setError(actionError.response?.data?.detail || 'Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    setConfirmDialog({
      show: true,
      title: 'Delete User',
      message: `Delete user ${user.email} permanently? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          setActionLoading(true);
          setError('');
          setSuccess('');
          const response = await axios.delete(`/api/user-management/users/${user.id}`);
          setSuccess(response.data?.message || 'User deleted permanently');
          await loadUsers(search, roleFilter);
        } catch (actionError) {
          setError(actionError.response?.data?.detail || 'Failed to delete user');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={12}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <h2 className="h4 mb-3">User Management</h2>
              <p className="text-muted mb-3">
                Search users by email or phone, and manage roles, password, suspension, and deletion.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={applySearch}>
                <Row className="g-2 align-items-start">
                  <Col md={5}>
                    <Form.Group>
                      <Form.Label>Search by Email or Phone</Form.Label>
                      <Form.Control
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Enter email or phone"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Role</Form.Label>
                      <Form.Select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                        <option value="">All Roles</option>
                        {ALL_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md="auto" className="pt-4">
                    <Button type="submit" className="btn-uniform-height">Apply Filter</Button>
                  </Col>
                  <Col md="auto" className="pt-4">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="btn-uniform-height"
                      onClick={clearSearch}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <h3 className="h5 mb-3">All Users</h3>
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" />
                </div>
              ) : users.length === 0 ? (
                <Alert variant="info" className="mb-0">No users found.</Alert>
              ) : (
                <Table responsive bordered hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Roles</th>
                      <th>Suspended</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const displayRoles = Array.isArray(user.roles) && user.roles.length > 0
                        ? user.roles
                        : [user.role];
                      return (
                        <tr key={user.id}>
                          <td>{user.name}</td>
                          <td style={{ color: user.email_verified ? 'green' : 'red' }}>{user.email}</td>
                          <td>{user.phone_number || '-'}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {displayRoles.map((r) => (
                                <Badge key={r} bg={roleVariant(r)} className="text-capitalize">
                                  {r.replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td>{user.is_suspended ? 'Yes' : 'No'}</td>
                          <td>{formatDateTime(user.created_at)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                className="btn-uniform-height"
                                disabled={actionLoading}
                                onClick={() => openRolesModal(user)}
                              >
                                Manage Roles
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="btn-uniform-height"
                                disabled={actionLoading}
                                onClick={() => openPasswordModal(user)}
                              >
                                Reset Password
                              </Button>
                              <Button
                                size="sm"
                                variant={user.is_suspended ? 'outline-success' : 'outline-warning'}
                                className="btn-uniform-height"
                                disabled={actionLoading}
                                onClick={() => toggleSuspendUser(user)}
                              >
                                {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="btn-uniform-height"
                                disabled={actionLoading}
                                onClick={() => handleDeleteUser(user)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Manage Roles Modal */}
      <Modal show={showRolesModal} onHide={() => setShowRolesModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage Roles — {selectedUser?.email}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Select one or more roles for this user. At least one role must be selected.
          </p>
          {ALL_ROLES.map((r) => (
            <Form.Check
              key={r.value}
              type="checkbox"
              id={`role-${r.value}`}
              label={r.label}
              checked={selectedRoles.includes(r.value)}
              onChange={() => toggleRole(r.value)}
              className="mb-2"
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="btn-uniform-height" onClick={() => setShowRolesModal(false)}>
            Cancel
          </Button>
          <Button
            className="btn-uniform-height"
            onClick={handleSaveRoles}
            disabled={actionLoading || selectedRoles.length === 0}
          >
            {actionLoading ? 'Saving...' : 'Save Roles'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="Enter new password"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="align-items-start">
          <Button
            variant="secondary"
            className="btn-uniform-height d-inline-flex align-items-center justify-content-center"
            onClick={() => setShowPasswordModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="btn-uniform-height d-inline-flex align-items-center justify-content-center"
            onClick={handleResetPassword}
            disabled={actionLoading}
          >
            {actionLoading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Dialog */}
      <Modal show={confirmDialog.show} onHide={closeConfirmDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmDialog.title || 'Confirm Action'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmDialog.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeConfirmDialog}>Cancel</Button>
          <Button variant="danger" onClick={executeConfirmDialogAction}>
            {confirmDialog.confirmText || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SuperUserUserManager;
